'use client';

import React from 'react';

import classNames from 'classnames';
import { useTranslations } from 'next-intl';
import Link from 'next/link';

import { useMT5ModalContext } from '@/blocks/components/MT5OfferingModal/Modal';
import { usePathname } from '@/i18n/routing';
import { EventBlockNames } from '@/providers/analytics/types';
import buttons from '@/styles/button.module.scss';
import link from '@/styles/link.module.scss';
import typography from '@/styles/typography.module.scss';
import { snakeCase } from '@/utils/string';

import { DocumentLink, LEGAL_DOCS } from '../DocumentLink';
import { IconCross } from '../Icons';
import { Dialog } from '../Modal/Dialog';
import styles from './MT5OfferingModal.module.scss';

type MT5OfferingModalProps = {
  utmContent: string;
};

/**
 * Analytics https://trade-team.atlassian.net/browse/PRXS-3047
 */
export const MT5OfferingModal = ({ utmContent }: MT5OfferingModalProps) => {
  const path = usePathname();
  const tr = useTranslations();
  const href = getMT5Link(utmContent, path);
  const { open, handleClose } = useMT5ModalContext();

  return (
    <Dialog open={open} onClose={handleClose} backdropBlurred className={styles.dialog}>
      <div className={styles.mt5Modal}>
        <div className={styles.mt5Heading}>
          <p className={typography.h4}>{tr.rich('modal.mt5.title')}</p>
          <div className={styles.mt5Close} onClick={handleClose}>
            <IconCross />
          </div>
        </div>
        <div className={styles.mt5Info}>
          <span className={typography.bodyM}>{tr.rich('modal.mt5.description')}</span>
          <span className={typography.bodyM}>
            {tr.rich('modal.mt5.agreement', {
              a: (content) => (
                <DocumentLink doc={LEGAL_DOCS.PXBT} className={classNames(link.textLink, link.underline)}>
                  {content}
                </DocumentLink>
              ),
            })}
          </span>
        </div>
        <div className={styles.mt5Corners}>
          <Link
            className={classNames(buttons.btn, buttons.btnM, buttons.btnPrimary)}
            href={href}
            target="_blank"
            data-event-label={tr('en.modal.mt5.cta')}
            data-event-block-name={EventBlockNames.modal_mt5}
          >
            {tr.rich('modal.mt5.cta')}
          </Link>
        </div>
      </div>
    </Dialog>
  );
};

function getMT5Link(utmContent: string, utmCampaign: string) {
  const url = 'https://pxbt.com/id/sign-up';

  const queryParams = {
    utm_source: 'primexbt',
    utm_medium: 'referral',
    utm_campaign: snakeCase(utmCampaign),
    utm_content: snakeCase(utmContent),
  };

  return `${url}?${new URLSearchParams(queryParams).toString()}`;
}
